import React from 'react';
import { useTranslator } from '../components/i18n/useTranslator';
import type { AxiosError } from 'axios';
import { ERROR_LOCALE_KEYS, type ErrorLocaleKey } from '../../common/error/errorLocaleKeys';

/**
 * Keys whose copy contains custom tags (e.g. <statusLink>), rendered as HTML so the tags stay
 * invisible. Fixed list: `html` feeds dangerouslySetInnerHTML, so keys with user input can't use it.
 */
const HTML_ERROR_LOCALE_KEYS: ReadonlySet<string> = new Set([
    ERROR_LOCALE_KEYS.AUTH.EMBARGOED_IP,
    ERROR_LOCALE_KEYS.NETWORK.CONNECTION_LOST,
    ERROR_LOCALE_KEYS.SERVER.GENERIC,
    ERROR_LOCALE_KEYS.SERVER.MAINTENANCE,
]);

/**
 * Shape of error objects returned from the API layer.
 *
 * This mirrors the structure used by the server and by client‑side error helpers:
 * - `errorLocaleKey`/`context` are used to look up a translated message
 * - `message` acts as a plain‑text fallback when no translation is available
 * - some errors (for example AppError instances) nest these fields inside `inner`
 */
export type ApiError = {
    message?: string;
    errorLocaleKey?: ErrorLocaleKey;
    context?: Record<string, unknown>;
    code?: string;
    status?: number;
    details?: Record<string, unknown>;
    // Some server errors store extra fields, including errorLocaleKey/context,
    // under an `inner` object to keep the top‑level response stable.
    inner?: {
        errorLocaleKey?: ErrorLocaleKey;
        context?: Record<string, unknown>;
        // Allow any additional properties without type errors
        [key: string]: unknown;
    };
};

/**
 * Narrow translation function interface used by the helpers below.
 *
 * This matches the simplest `useTranslator('errors')` signature so that
 * callers can pass either the real translator or a lightweight test double.
 */
export type TranslationFunction = (key: string, context?: Record<string, unknown>) => string;

/**
 * Hook used in client components to turn an `ApiError` into user‑facing content.
 *
 * Behaviour:
 * - `errorLocaleKey` (top‑level or in `inner`) is translated; tag-bearing copy renders as HTML
 * - otherwise `message` is returned unchanged
 * - with neither, falls back to the generic server error; with no error at all, “unknown error”
 *
 * Typical usage:
 *   const translateError = useErrorTranslator();
 *   const message = translateError(apiErrorOrValidationError);
 */
export const useErrorTranslator = () => {
    const translate = useTranslator('errors');

    return (error?: ApiError): string | React.ReactElement => {
        if (!error) {
            // Use a translation key for unknown errors with English default text
            return translate('server.unknown');
        }

        const localeKey = error?.errorLocaleKey || error.inner?.errorLocaleKey;
        const context = error?.context || error.inner?.context || {};

        // If server provides errorLocaleKey (either top-level or in inner), use it for translation
        if (localeKey) {
            return HTML_ERROR_LOCALE_KEYS.has(localeKey)
                ? translate(localeKey as never, context, { html: true })
                : (translate(localeKey as never, context) as string);
        }

        if (error?.message) return error.message;

        return translate(ERROR_LOCALE_KEYS.SERVER.GENERIC, undefined, { html: true });
    };
};

/**
 * Convenience helper for translating an Axios error response using the `errors` namespace.
 *
 * Expects the server to respond with `{ error: ApiError }` and handles:
 * - missing `response`/`error` by returning either the standard “unknown error” translation
 *   or the provided `fallbackMessage` when supplied
 * - `errorLocaleKey`/`context` (top‑level or `inner`) when present
 * - falling back to `error.message` when no translation key is provided, or to `fallbackMessage`
 *   when there is no message
 *
 * Usage:
 *   const translate = useTranslator('errors');
 *   const message = getTranslatedErrorMessage(axiosError, translate);
 *   const messageWithFallback = getTranslatedErrorMessage(axiosError, translate, 'Custom fallback message');
 */
export const getTranslatedErrorMessage = (
    err: AxiosError<{ error?: ApiError }>,
    translate: TranslationFunction,
    fallbackMessage?: string,
): string => {
    const error = err.response?.data?.error;

    // No error payload – either use custom fallback or generic unknown error
    if (!error) {
        return fallbackMessage ?? translate('server.unknown');
    }

    const localeKey = error.errorLocaleKey ?? error.inner?.errorLocaleKey;
    const context = error.context ?? error.inner?.context;

    if (localeKey) {
        return translate(localeKey, context ?? {});
    }

    // No locale key – prefer the server message, then the custom fallback, then the generic unknown error.
    return error.message ?? fallbackMessage ?? translate('server.unknown');
};
